import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { api } from '../lib/api';
import { useServices } from '../hooks/useOffers';
import { ApiError } from '../lib/api';
import { CATEGORY_KEYS, categoryLabel, formatPrice, sizeLabel } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { Lang, ServiceDto, ServicePriceDto, SizeKey } from '@shared/types';

const SIZE_KEYS: SizeKey[] = ['flat', 'studio', '1br', '2br', '3br'];

/** Prázdná služba pro formulář „nová položka katalogu". */
const blank = (): ServiceDto => ({
  id: '',
  key: '',
  category: 'ownership',
  orderIndex: 0,
  hasSizeTiers: false,
  defaultVatRate: 5,
  isActive: true,
  texts: {
    cs: { name: '', eyebrow: '', lead: '', includesMd: '', conditionsMd: '' },
    en: { name: '', eyebrow: '', lead: '', includesMd: '', conditionsMd: '' },
  },
  prices: [{ id: 'new', sizeKey: 'flat', amount: 0, isFrom: false, sublabelCs: '', sublabelEn: '', orderIndex: 0 }],
});

export default function Services() {
  const { t, lang: uiLang } = useI18n();
  const { data: services = [], isLoading } = useServices();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ServiceDto | null>(null);

  const selected = useMemo(
    () => (draft?.id === '' ? draft : services.find((s) => s.id === selectedId) ?? null),
    [draft, services, selectedId],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, ServiceDto[]>();
    for (const s of services) {
      const list = map.get(s.category) ?? [];
      list.push(s);
      map.set(s.category, list);
    }
    return [...map.entries()];
  }, [services]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-dark">{t('services.title')}</h1>
        <button
          className="btn-primary"
          onClick={() => {
            setSelectedId(null);
            setDraft(blank());
          }}
        >
          <Plus size={16} /> {t('services.new')}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,340px)_1fr]">
        <div className="card divide-y divide-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="p-5 text-sm text-gray-500">{t('common.loading')}</div>
          ) : services.length === 0 ? (
            <div className="p-5 text-sm text-gray-500">
              {t('services.empty')}
            </div>
          ) : (
            grouped.map(([category, list]) => (
              <div key={category} className="p-3">
                <div className="label px-2">{categoryLabel(t, category)}</div>
                {list.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setDraft(null);
                      setSelectedId(s.id);
                    }}
                    className={`flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-teal-light ${
                      selectedId === s.id ? 'bg-teal-light text-teal' : ''
                    }`}
                  >
                    <span className="truncate">
                      {s.texts[uiLang].name || s.texts.cs.name || s.key}
                      {!s.isActive && <span className="ml-2 text-xs text-gray-400">{t('services.hidden')}</span>}
                    </span>
                    <ChevronRight size={14} className="shrink-0 text-gray-300" />
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        {selected ? (
          <ServiceForm
            key={selected.id || 'new'}
            service={selected}
            onDone={(id) => {
              setDraft(null);
              setSelectedId(id);
            }}
          />
        ) : (
          <div className="card flex items-center justify-center p-16 text-sm text-gray-500">
            {t('services.selectHint')}
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceForm({ service, onDone }: { service: ServiceDto; onDone: (id: string | null) => void }) {
  const qc = useQueryClient();
  const { t, lang: uiLang } = useI18n();
  const [form, setForm] = useState<ServiceDto>(service);
  const [lang, setLang] = useState<Lang>('cs');
  const isNew = form.id === '';

  const invalidate = () => qc.invalidateQueries({ queryKey: ['services'] });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        key: form.key,
        category: form.category,
        orderIndex: form.orderIndex,
        hasSizeTiers: form.hasSizeTiers,
        defaultVatRate: form.defaultVatRate,
        isActive: form.isActive,
        texts: form.texts,
        prices: form.prices.map((p, i) => ({
          sizeKey: p.sizeKey,
          amount: p.amount,
          isFrom: p.isFrom,
          sublabelCs: p.sublabelCs,
          sublabelEn: p.sublabelEn,
          orderIndex: i,
        })),
      };
      return isNew
        ? api.post<{ data: ServiceDto }>('/services', body).then((r) => r.data)
        : api.patch<{ data: ServiceDto }>(`/services/${form.id}`, body).then((r) => r.data);
    },
    onSuccess: (saved) => {
      invalidate();
      onDone(saved.id);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/services/${form.id}`),
    onSuccess: () => {
      invalidate();
      onDone(null);
    },
  });

  const setText = (key: keyof ServiceDto['texts'][Lang], value: string) =>
    setForm((f) => ({ ...f, texts: { ...f.texts, [lang]: { ...f.texts[lang], [key]: value } } }));

  const setPrice = (index: number, patch: Partial<ServicePriceDto>) =>
    setForm((f) => ({
      ...f,
      prices: f.prices.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }));

  return (
    <div className="card space-y-5 p-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex-1">
          <span className="label">{t('services.key')}</span>
          <input
            className="input"
            value={form.key}
            placeholder="snagging"
            onChange={(e) => setForm({ ...form, key: e.target.value })}
          />
        </label>
        <label>
          <span className="label">{t('services.category')}</span>
          <select
            className="input w-auto"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            {CATEGORY_KEYS.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(t, c)}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="label">{t('services.order')}</span>
          <input
            type="number"
            className="input w-24"
            value={form.orderIndex}
            onChange={(e) => setForm({ ...form, orderIndex: Number(e.target.value) })}
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-5 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.hasSizeTiers}
            onChange={(e) => setForm({ ...form, hasSizeTiers: e.target.checked })}
          />
          {t('services.sizeTiers')}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          {t('services.active')}
        </label>
        <label className="flex items-center gap-2">
          {t('services.vatRate')}
          <input
            type="number"
            className="input w-20"
            value={form.defaultVatRate}
            onChange={(e) => setForm({ ...form, defaultVatRate: Number(e.target.value) })}
          />
          %
        </label>
      </div>

      <div>
        <div className="mb-3 flex gap-1 border-b border-gray-200">
          {(['cs', 'en'] as Lang[]).map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                lang === l ? 'border-b-2 border-teal text-teal' : 'text-gray-500 hover:text-teal'
              }`}
            >
              {l === 'cs' ? t('common.langCs') : t('common.langEn')}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label>
              <span className="label">{t('services.name')}</span>
              <input
                className="input"
                value={form.texts[lang].name}
                onChange={(e) => setText('name', e.target.value)}
              />
            </label>
            <label>
              <span className="label">{t('services.eyebrow')}</span>
              <input
                className="input"
                placeholder={t('services.eyebrowPlaceholder')}
                value={form.texts[lang].eyebrow}
                onChange={(e) => setText('eyebrow', e.target.value)}
              />
            </label>
          </div>
          <label className="block">
            <span className="label">{t('common.lead')}</span>
            <textarea
              className="input"
              rows={3}
              value={form.texts[lang].lead}
              onChange={(e) => setText('lead', e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">{t('services.includes')}</span>
            <textarea
              className="input font-mono text-xs"
              rows={6}
              value={form.texts[lang].includesMd}
              onChange={(e) => setText('includesMd', e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">{t('services.conditions')}</span>
            <textarea
              className="input font-mono text-xs"
              rows={4}
              value={form.texts[lang].conditionsMd}
              onChange={(e) => setText('conditionsMd', e.target.value)}
            />
          </label>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <div className="label mb-0">{t('services.prices')}</div>
          <button
            className="btn-ghost text-xs"
            onClick={() =>
              setForm({
                ...form,
                prices: [
                  ...form.prices,
                  {
                    id: `new-${form.prices.length}`,
                    sizeKey: 'flat',
                    amount: 0,
                    isFrom: false,
                    sublabelCs: '',
                    sublabelEn: '',
                    orderIndex: form.prices.length,
                  },
                ],
              })
            }
          >
            <Plus size={14} /> {t('services.addPrice')}
          </button>
        </div>

        <div className="space-y-2">
          {form.prices.map((p, i) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 p-2">
              <select
                className="input w-32"
                value={p.sizeKey}
                onChange={(e) => setPrice(i, { sizeKey: e.target.value as SizeKey })}
              >
                {SIZE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {sizeLabel(t, k)}
                  </option>
                ))}
              </select>
              <input
                type="number"
                className="input w-32"
                value={p.amount}
                onChange={(e) => setPrice(i, { amount: Number(e.target.value) })}
              />
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={p.isFrom}
                  onChange={(e) => setPrice(i, { isFrom: e.target.checked })}
                />
                {t('services.from')}
              </label>
              <input
                className="input flex-1"
                placeholder={lang === 'cs' ? t('services.sublabelCs') : t('services.sublabelEn')}
                value={lang === 'cs' ? p.sublabelCs : p.sublabelEn}
                onChange={(e) =>
                  setPrice(i, lang === 'cs' ? { sublabelCs: e.target.value } : { sublabelEn: e.target.value })
                }
              />
              <span className="text-xs text-gray-500">{formatPrice(p.amount, p.isFrom, uiLang)}</span>
              <button
                className="text-gray-300 hover:text-danger"
                onClick={() => setForm({ ...form, prices: form.prices.filter((_, x) => x !== i) })}
                aria-label={t('services.removePrice')}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 border-t border-gray-200 pt-4">
        <button className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending || !form.key}>
          {save.isPending
            ? t('common.saving')
            : isNew
              ? t('services.create')
              : t('services.saveChanges')}
        </button>
        {!isNew && (
          <button
            className="btn-ghost text-danger"
            onClick={() => {
              if (confirm(t('services.confirmDelete'))) {
                remove.mutate();
              }
            }}
          >
            <Trash2 size={15} /> {t('common.delete')}
          </button>
        )}
        {save.isError && (
          <span className="text-sm text-danger">
            {save.error instanceof ApiError && save.error.code === 'KEY_TAKEN'
              ? t('services.keyTaken')
              : (save.error as Error).message}
          </span>
        )}
      </div>
    </div>
  );
}
